/**
 * Códigos ISO 4217 das principais moedas usadas no ERP.
 *
 * Mantido como `enum` (com `string` literal) para permitir uso em `[ngValue]`,
 * comparações e payloads — o backend recebe o código ISO em maiúsculo.
 *
 * Os metadados de cada moeda (símbolo / locale para `Intl.NumberFormat`) ficam
 * em `CURRENCY_METADATA`, separados para não inflar o enum.
 */
export enum CurrencyCode {
  BRL = 'BRL',
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  ARS = 'ARS',
  CLP = 'CLP',
  MXN = 'MXN',
  PYG = 'PYG',
  UYU = 'UYU',
  CAD = 'CAD',
  JPY = 'JPY',
  CHF = 'CHF',
  CNY = 'CNY'
}

export interface CurrencyMetadata {
  code: CurrencyCode;
  /** Nome amigável em português usado nos selects da interface. */
  label: string;
  /** Símbolo monetário curto (ex.: "R$", "US$", "€"). */
  symbol: string;
  /** Locale BCP 47 para `Intl.NumberFormat` ao exibir valores. */
  locale: string;
}

/** Lista completa em ordem alfabética com BRL fixo no topo (padrão do ERP). */
export const CURRENCY_METADATA: readonly CurrencyMetadata[] = [
  { code: CurrencyCode.BRL, label: 'Real brasileiro (BRL)',    symbol: 'R$',  locale: 'pt-BR' },
  { code: CurrencyCode.USD, label: 'Dólar americano (USD)',    symbol: 'US$', locale: 'en-US' },
  { code: CurrencyCode.EUR, label: 'Euro (EUR)',                symbol: '€',   locale: 'de-DE' },
  { code: CurrencyCode.GBP, label: 'Libra esterlina (GBP)',     symbol: '£',   locale: 'en-GB' },
  { code: CurrencyCode.ARS, label: 'Peso argentino (ARS)',      symbol: '$',   locale: 'es-AR' },
  { code: CurrencyCode.CAD, label: 'Dólar canadense (CAD)',     symbol: 'C$',  locale: 'en-CA' },
  { code: CurrencyCode.CHF, label: 'Franco suíço (CHF)',        symbol: 'CHF', locale: 'de-CH' },
  { code: CurrencyCode.CLP, label: 'Peso chileno (CLP)',        symbol: '$',   locale: 'es-CL' },
  { code: CurrencyCode.CNY, label: 'Yuan chinês (CNY)',         symbol: '¥',   locale: 'zh-CN' },
  { code: CurrencyCode.JPY, label: 'Iene japonês (JPY)',        symbol: '¥',   locale: 'ja-JP' },
  { code: CurrencyCode.MXN, label: 'Peso mexicano (MXN)',       symbol: '$',   locale: 'es-MX' },
  { code: CurrencyCode.PYG, label: 'Guarani paraguaio (PYG)',   symbol: '₲',   locale: 'es-PY' },
  { code: CurrencyCode.UYU, label: 'Peso uruguaio (UYU)',       symbol: '$U',  locale: 'es-UY' }
] as const;

/** Lookup O(1) por código ISO. */
export const CURRENCY_BY_CODE: Readonly<Record<string, CurrencyMetadata>> =
  CURRENCY_METADATA.reduce<Record<string, CurrencyMetadata>>((acc, m) => {
    acc[m.code] = m;
    return acc;
  }, {});

/** Helper amigável para formulários (`*ngFor="let c of CURRENCY_OPTIONS"`). */
export const CURRENCY_OPTIONS = CURRENCY_METADATA;
